import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { OwnerExtendedData } from './dto/owner.extended.data';
import { PortalPropietarioService } from './portal-propietario.service';

@Injectable({
  providedIn: 'root'
})
export class PostsalePortalRoutingService {

  private static readonly LOCAL_ISSUES_ROUTE: string[] = ['/main/listado-incidencias'];
  private static readonly BARE_ASCII_HOSTNAME_PATTERN: RegExp =
    /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

  constructor(private router: Router,
              private portalPropietarioService: PortalPropietarioService,
              @Inject(DOCUMENT) private document: Document) { }

  public async routeAfterClassicLogin(): Promise<void> {
    let targetOrigin: string = null;

    try {
      const ownerExtendedData: OwnerExtendedData = await this.getOwnerExtendedData();
      targetOrigin = this.getAllowedOrigin(ownerExtendedData && ownerExtendedData.csite
        ? ownerExtendedData.csite.postsale_portal_url : null);
    } catch (error) {
      targetOrigin = null;
    }

    if (!targetOrigin) {
      targetOrigin = this.getAllowedOrigin(environment.postsalePortalFallbackUrl);
    }

    if (!targetOrigin || targetOrigin === this.document.location.origin) {
      await this.navigateToLocalIssues();
      return;
    }

    this.document.location.replace(new URL('/login', targetOrigin).toString());
  }

  private getOwnerExtendedData(): Promise<OwnerExtendedData> {
    return new Promise((resolve: (ownerExtendedData: OwnerExtendedData) => void, reject: (error: any) => void) => {
      this.portalPropietarioService.getOwnerExtendedDataCached().subscribe(resolve, reject);
    });
  }

  private getAllowedOrigin(urlToValidate: string): string {
    if (!urlToValidate) {
      return null;
    }

    try {
      const normalizedUrl: string = this.normalizeBareHostname(urlToValidate);
      if (!/^https:\/\//i.test(normalizedUrl) || /\s/.test(normalizedUrl) || this.hasExplicitPort(normalizedUrl) ||
          this.hasPathQueryOrFragment(normalizedUrl)) {
        return null;
      }

      const parsedUrl: URL = new URL(normalizedUrl);
      if (parsedUrl.protocol !== 'https:' || parsedUrl.username || parsedUrl.password || parsedUrl.port ||
          parsedUrl.hostname.endsWith('.') || environment.postsalePortalAllowedHosts.indexOf(parsedUrl.hostname) === -1) {
        return null;
      }
      return parsedUrl.origin;
    } catch (error) {
      return null;
    }
  }

  private normalizeBareHostname(urlToValidate: string): string {
    return PostsalePortalRoutingService.BARE_ASCII_HOSTNAME_PATTERN.test(urlToValidate)
      ? 'https://' + urlToValidate : urlToValidate;
  }

  private hasExplicitPort(urlToValidate: string): boolean {
    const authority: string = this.getUrlAuthority(urlToValidate);
    return authority.lastIndexOf(':') !== -1;
  }

  private hasPathQueryOrFragment(urlToValidate: string): boolean {
    const authority: string = this.getUrlAuthority(urlToValidate);
    return urlToValidate.length > urlToValidate.indexOf(authority) + authority.length;
  }

  private getUrlAuthority(urlToValidate: string): string {
    return urlToValidate.slice(urlToValidate.indexOf('://') + 3).split(/[/?#]/)[0];
  }

  private async navigateToLocalIssues(): Promise<void> {
    await this.router.navigate(PostsalePortalRoutingService.LOCAL_ISSUES_ROUTE);
  }
}
